// Refactored drei Svg component
// * added z-levels to avoid marker flickering
import {
  type MeshBasicMaterialProps,
  type MeshProps,
  type Object3DProps,
  useLoader,
} from '@react-three/fiber';
import { forwardRef, Fragment, useEffect, useMemo } from 'react';
import { DoubleSide, Object3D, ShapeGeometry } from 'three';
import { SVGLoader } from 'three-stdlib';
import type { ForwardRefComponent } from '../types';

export interface SvgProps extends Omit<Object3DProps, 'ref'> {
  /** src can be a URL or SVG data */
  src: string;
  skipFill?: boolean;
  skipStrokes?: boolean;
  fillMaterial?: MeshBasicMaterialProps;
  strokeMaterial?: MeshBasicMaterialProps;
  fillMeshProps?: MeshProps;
  strokeMeshProps?: MeshProps;
}

const Z_STEP = 0.0001;

export const Svg: ForwardRefComponent<SvgProps, Object3D> = forwardRef<Object3D, SvgProps>(
  function R3FSvg(
    {
      src,
      skipFill,
      skipStrokes,
      fillMaterial,
      strokeMaterial,
      fillMeshProps,
      strokeMeshProps,
      ...props
    },
    ref
  ) {
    const svg = useLoader(
      SVGLoader,
      !src.startsWith('<svg') ? src : `data:image/svg+xml;utf8,${src}`
    );

    /*
      z-levels separated by z_shift value (in order):
        0. path0.shapes
        1. path0.stroke
        2. path1.shape
        3. path1.stroke
        ...
    */

    const shapeGeometries = useMemo(() => {
      return svg.paths.map((path, index1) =>
        SVGLoader.createShapes(path).map((shape) => {
          const z_shift = 2 * index1 * Z_STEP;
          const geometry = new ShapeGeometry(shape);
          geometry.translate(0, 0, z_shift);
          return geometry;
        })
      );
    }, [svg]);

    useEffect(() => {
      return () => shapeGeometries.forEach((group) => group.forEach((g) => g.dispose()));
    }, [shapeGeometries]);

    const strokeGeometries = useMemo(() => {
      return skipStrokes
        ? []
        : svg.paths.map((path, index1) =>
            path.userData?.style.stroke === undefined || path.userData.style.stroke === 'none'
              ? null
              : path.subPaths.map((subPath) => {
                  const geometry = SVGLoader.pointsToStroke(
                    subPath.getPoints(),
                    path.userData!.style
                  );
                  const z_shift = (2 * index1 + 1) * Z_STEP;
                  geometry.translate(0, 0, z_shift);
                  return geometry;
                })
          );
    }, [svg, skipStrokes]);

    useEffect(() => {
      return () => strokeGeometries.forEach((group) => group?.map((g) => g.dispose()));
    }, [strokeGeometries]);

    return (
      <object3D ref={ref} {...props}>
        <object3D scale={[1, -1, 1]}>
          {svg.paths.map((path, p) => (
            <Fragment key={p}>
              {!skipFill &&
                path.userData?.style.fill !== undefined &&
                path.userData.style.fill !== 'none' &&
                shapeGeometries[p].map((shapeGeometry, s) => (
                  <mesh key={s} {...fillMeshProps} geometry={shapeGeometry}>
                    <meshBasicMaterial
                      color={path.userData!.style.fill}
                      opacity={path.userData!.style.fillOpacity}
                      transparent={true}
                      side={DoubleSide}
                      // depthTest={false}
                      depthWrite={false}
                      {...fillMaterial}
                    />
                  </mesh>
                ))}
              {!skipStrokes &&
                path.userData?.style.stroke !== undefined &&
                path.userData.style.stroke !== 'none' &&
                path.subPaths.map((_subPath, s) => (
                  <mesh key={s} geometry={strokeGeometries[p]![s]} {...strokeMeshProps}>
                    <meshBasicMaterial
                      color={path.userData!.style.stroke}
                      opacity={path.userData!.style.strokeOpacity}
                      transparent={true}
                      side={DoubleSide}
                      // depthTest={false}
                      depthWrite={false}
                      {...strokeMaterial}
                    />
                  </mesh>
                ))}
            </Fragment>
          ))}
        </object3D>
      </object3D>
    );
  }
);
